/**
 * Date formatting utilities for the Indoor Cycling Playlists app
 * 
 * All dates are stored in the database as UTC timestamps.
 * These functions format dates without timezone conversion to ensure
 * consistent display across different server/client timezones.
 */

/**
 * Format a date string as "Month Day, Year" (e.g., "October 30, 2025")
 * @param {string} dateString - ISO date string (e.g., "2025-10-30T00:00:00.000Z")
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  return `${monthNames[month]} ${day}, ${year}`;
};

/**
 * Format a date string as "Mon Day, Year" (e.g., "Oct 30, 2025")
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted short date string
 */
export const formatShortDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  return `${monthNames[month]} ${day}, ${year}`;
};

/**
 * Format a date string as "Mon Day" without year (e.g., "Oct 30")
 * Useful for compact displays
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted short date string without year
 */
export const formatShortDateNoYear = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  return `${monthNames[month]} ${day}`;
};

/**
 * Format a date string as ISO date only (YYYY-MM-DD)
 * @param {string} dateString - ISO date string
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatISODate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Check if a date is in the future (useful for upcoming classes)
 * @param {string} dateString - ISO date string
 * @returns {boolean} True if date is in the future
 */
export const isFutureDate = (dateString) => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  const today = new Date();
  
  // Set both to midnight UTC for fair comparison
  date.setUTCHours(0, 0, 0, 0);
  today.setUTCHours(0, 0, 0, 0);
  
  return date > today;
};

/**
 * Get relative time description (e.g., "2 days ago", "in 3 days")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time description
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const today = new Date();
  
  // Set both to midnight UTC
  date.setUTCHours(0, 0, 0, 0);
  today.setUTCHours(0, 0, 0, 0);
  
  const diffTime = date - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1) return `In ${diffDays} days`;
  if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
  
  return formatShortDate(dateString);
};